/**
 * AtlasCrittersModule — the close-up life of the ambient life wave (2026-09-29): SUNWING butterflies over the meadows by
 * day, GLASSDART dragonflies over still water, TIDECLACK crabs on the sea's beaches. Rules: `lib/ambient_critters.ts`
 * (pure). Bodies + the wing / leg animation: `lib/ambient_models.ts` (LOD2 — a 9 cm butterfly needs 374 triangles).
 *
 * Every critter lives in a bubble around the viewer (the pollen's law: the field is everywhere, the bubble moves),
 * sized by the tier's `particleCap`, CPU-stepped on the WALL clock, drawn as one instanced mesh per kind under an
 * anchor group near the viewer (THE FRAME). Water is found by looking: rays out from the viewer for still water
 * (a lake, a river, a pond — `waterStillAt` / the baked lakes) and for the SEA's waterline (`waterKindAt`).
 * Under the `ambientLife` switch; off indoors, aloft and in VR.
 */
import * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex/core/App.js';
import { makeSystem, Phase } from '../engine-compat.js';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import type { EnvironmentModule } from './EnvironmentModule.js';
import type { WeatherModule } from './WeatherModule.js';
import type { AssetModule } from './AssetModule.js';
import { uSunDir } from './SkyModule.js';
import { nightFactor } from '../lib/lighting.js';
import { forestDensity } from '../lib/forest_field.js';
import { commonsSeaLevel } from '../lib/sea_level.js';
import { waterStillAt, waterKindAt } from '../lib/water_fx.js';
import { lakeSurfaceAtBaked } from '../lib/lakes.js';
import { biomeAt } from '../../../shared/terrain.js';
import { activePreset } from '../lib/quality_tiers.js';
import { sfxAt } from '../lib/sfx.js';
import { POLLEN, POLLEN_BIOMES } from '../lib/ambient_life.js';
import {
  SUNWING, GLASSDART, TIDECLACK, critterBudget, spawnSunwing, stepSunwing, sunwingDone, sunwingWingAngle, wingAngleAt, spawnGlassdart, stepGlassdart,
  spawnTideclack, stepTideclack, type CritterBudget, type CritterEnv, type Sunwing, type Glassdart, type Tideclack, type WaterPoint, type ShorePoint,
} from '../lib/ambient_critters.js';
import { creatureParts, CreatureBatch, type CreatureKind } from '../lib/ambient_models.js';

const REANCHOR_M = 800;
const ENV_EVERY_S = 1;
const WATER_EVERY_S = 3;
const ALOFT_M = 45;
const SCOPE_SOUND_COOLDOWN = { buzz: 1200, click: 600 };

type Kind = 'sunwing' | 'glassdart' | 'tideclack';
const KIND_OF: Record<Kind, { prefab: string; kind: CreatureKind; layFlat: boolean; tinted: boolean; cap: number }> = {
  sunwing: { prefab: 'prop:critter_sunwing', kind: 'wings', layFlat: true, tinted: true, cap: 20 },
  glassdart: { prefab: 'prop:critter_glassdart', kind: 'wings', layFlat: false, tinted: false, cap: 12 },
  tideclack: { prefab: 'prop:critter_tideclack', kind: 'crab', layFlat: false, tinted: false, cap: 14 },
};

interface Slot { batch: CreatureBatch | null; loading: boolean; failed: number; retryAt?: number }

export class AtlasCrittersModule implements IModule {
  readonly name = 'AtlasCritters';
  private app: App | null = null;
  private scene: THREE.Scene | null = null;
  private env: EnvironmentModule | null = null;
  private weather: WeatherModule | null = null;
  private assets: AssetModule | null = null;
  private seed = '';
  private sea = 0;
  private anchor: THREE.Group | null = null;
  private ax = 0; private ay = 0; private az = 0;
  private readonly slots = new Map<Kind, Slot>();
  private sunwings: Sunwing[] = [];
  private glassdarts: Glassdart[] = [];
  private tideclacks: Tideclack[] = [];
  private waterPts: WaterPoint[] = [];
  private shorePts: ShorePoint[] = [];
  private budget: CritterBudget = { sunwing: 0, glassdart: 0, tideclack: 0, why: 'not started' };
  private lastEnv: CritterEnv | null = null;
  private gate: string | null = 'not started';
  private envIn = 0;
  private waterIn = 0;
  private lastMs = 0;
  private lastBuzz = 0;
  private lastClick = 0;
  private disposed = false;
  /** The bodies (LOD2, a few hundred triangles each) are asked for as soon as the layer is live — THE QUEUE LAW. */
  private prefetched = false;
  private rngState = 0x2545f491;
  private readonly rng = (): number => { let t = (this.rngState += 0x6d2b79f5); t = Math.imul(t ^ (t >>> 15), t | 1); t ^= t + Math.imul(t ^ (t >>> 7), t | 61); return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
  private readonly stats = { sunwingsSpawned: 0, perched: 0, glassdartsSpawned: 0, darts: 0, tideclacksSpawned: 0, crabsFled: 0, buzzes: 0, clicks: 0 };

  init(app: App): void {
    this.app = app;
    this.scene = app.world.scene as THREE.Scene;
    bus.on('atlas:world-entered', () => this.onWorld());
    bus.on('atlas:environment-updated', () => this.onWorld());
    app.world.addSystem(makeSystem('AtlasCritters', Phase.Animation, 44, (dt) => this.tick(dt)));
    const g = globalThis as unknown as Record<string, unknown>;
    g.__critters = () => this.diag();
    /**
     * Probe lever: `n` critters of a kind around the viewer now, whatever the place (the flag and a particle budget still
     * apply); with `dist`, gathered `dist` metres in front of the camera (a picture of a 9 cm butterfly needs it close).
     */
    g.__crittersForce = (kind: Kind, n = 6, dist?: number, tame = false) => this.force(kind, n, dist, tame);
  }

  private onWorld(): void {
    let seed = '';
    try { const conn = atlas.conn, id = atlas.currentWorldId; if (conn && id !== null) { const w = conn.db.world.worldId.find(id); if (w && w.kind === 'commons') seed = w.terrainSeed; } } catch { /* churn */ }
    if (seed === this.seed) return;
    this.clearAll();
    this.seed = seed;
    this.sea = seed ? commonsSeaLevel(seed) : 0;
  }

  private groundAt(x: number, z: number): number { return this.env ? this.env.groundAt(x, z) : 0; }
  private stillAt(x: number, z: number): number | null { return waterStillAt(x, z) ?? lakeSurfaceAtBaked(x, z); }

  // ─── the tick ──────────────────────────────────────────────────────────────
  private tick(dtRaw: number): void {
    if (!this.seed && atlas.currentWorldId !== null) this.onWorld();
    const app = this.app;
    if (app) {
      this.env ??= (app.getModule?.('Environment') as EnvironmentModule | undefined) ?? null;
      this.weather ??= (app.getModule?.('Weather') as WeatherModule | undefined) ?? null;
      this.assets ??= (app.getModule?.('AssetModule') as AssetModule | undefined) ?? null;
    }
    const p = activePreset();
    this.gate = p.ambientLife === false ? 'ambientLife off'
      : !this.seed ? 'no world seed yet'
        : atlas.loadingGate ? 'loading gate is up'
          : atlas.currentWorldId !== atlas.commonsWorldId ? 'not in the Commons'
            : atlas.spaceFlight ? 'in space flight'
              : !this.env ? 'modules not resolved yet'
                : null;
    if (this.gate) { this.clearAll(); return; }
    const now = performance.now();
    const dt = Math.min(0.25, Math.max(0, this.lastMs ? (now - this.lastMs) / 1000 : dtRaw)); // AMBIENT TIME: the wall clock
    this.lastMs = now;
    const px = atlas.localX, py = atlas.localY, pz = atlas.localZ;
    const w = this.weather?.wind();
    this.waterIn -= dt;
    if (this.waterIn <= 0) { this.waterIn = WATER_EVERY_S; this.scanWater(px, pz); }
    this.envIn -= dt;
    if (this.envIn <= 0) {
      this.envIn = ENV_EVERY_S;
      const gy = this.groundAt(px, pz);
      const night = nightFactor((uSunDir.value as THREE.Vector3).y);
      const precipKind = w?.targetKind ?? 'none', precip = w?.targetDensity ?? 0;
      const temp = w?.temperatureC ?? null;
      const falling = (precipKind === 'rain' || precipKind === 'snow') ? precip : 0;
      const fd = forestDensity(this.seed, px, pz, this.sea);
      const meadowByDay = POLLEN_BIOMES.has(biomeAt(this.seed, px, pz)) && fd < POLLEN.forestMax && night <= POLLEN.nightMax
        && falling <= POLLEN.precipMax && precipKind !== 'dust' && (temp ?? 18) >= POLLEN.tempMin && (p.foliageDensityScale ?? 1) > 0;
      const e: CritterEnv = {
        enabled: p.ambientLife, particleCap: p.particleCap ?? 0, meadowByDay, night, precipKind, precip, temperatureC: temp,
        indoors: atlas.interiorId !== null, away: py - gy > ALOFT_M, waterPoints: this.waterPts.length, seaShorePoints: this.shorePts.length,
      };
      this.lastEnv = e;
      this.budget = critterBudget(e);
    }
    this.ensureAnchor(px, py, pz);
    if (!this.prefetched && this.assets && this.anchor) {
      // THE QUEUE LAW (AtlasHerdsModule.prefetch): the load gate is a FIFO shared with the world's streaming — get in line now
      this.prefetched = true;
      for (const k of ['sunwing', 'glassdart', 'tideclack'] as Kind[]) {
        let slot = this.slots.get(k);
        if (!slot) { slot = { batch: null, loading: false, failed: 0 }; this.slots.set(k, slot); }
        if (!slot.batch && !slot.loading) void this.load(k, slot);
      }
    }
    const near = { x: px, z: pz };
    this.stepSunwings(dt, px, pz, near);
    this.stepGlassdarts(dt, px, py, pz);
    this.stepTideclacks(dt, px, pz, near);
    this.draw();
  }

  /** Still water and the sea's waterline around the viewer: 12 rays out to their search radius, every few seconds. */
  private scanWater(px: number, pz: number): void {
    const water: WaterPoint[] = [], shores: ShorePoint[] = [];
    const wetAt = (x: number, z: number): number | null => { const l = this.stillAt(x, z); return l !== null && this.groundAt(x, z) < l - 0.15 ? l : null; };
    for (let k = 0; k < 12; k++) {
      const a = (k / 12) * Math.PI * 2, ca = Math.cos(a), sa = Math.sin(a);
      let dryX = px, dryZ = pz, wasDry = wetAt(px, pz) === null, foundWater = false;
      const reach = Math.max(GLASSDART.searchM, TIDECLACK.searchM);
      for (let d = 5; d <= reach; d += 5) {
        const x = px + ca * d, z = pz + sa * d;
        const lvl = wetAt(x, z);
        if (lvl !== null) {
          if (!foundWater && d <= GLASSDART.searchM && waterKindAt(x, z) !== 'sea') { water.push({ x, z, y: lvl }); foundWater = true; }
          if (wasDry && waterKindAt(x, z) === 'sea') {
            const gy = this.groundAt(dryX, dryZ);
            if (gy >= lvl - 0.3 && gy <= lvl + 2.5) shores.push({ x: dryX, z: dryZ, y: gy, sx: ca, sz: sa });
          }
          break;
        }
        wasDry = true; dryX = x; dryZ = z;
      }
    }
    this.waterPts = water;
    this.shorePts = shores;
  }

  private stepSunwings(dt: number, px: number, pz: number, near: { x: number; z: number }): void {
    const target = Math.min(this.budget.sunwing, KIND_OF.sunwing.cap);
    while (this.sunwings.length < target) { this.sunwings.push(spawnSunwing(this.rng, px, pz, (x, z) => this.groundAt(x, z))); this.stats.sunwingsSpawned++; }
    // a budget that fell (dusk, rain, leaving the meadow) lets the extras fly off the bubble's edge rather than vanish
    let perched = 0;
    for (let i = this.sunwings.length - 1; i >= 0; i--) {
      const s = this.sunwings[i];
      stepSunwing(s, dt, this.rng, this.groundAt(s.x, s.z), px, pz, s.tame ? null : near);
      if (s.perched) perched++;
      if (sunwingDone(s, px, pz) || (i >= target && Math.hypot(s.x - px, s.z - pz) > SUNWING.radius)) this.sunwings.splice(i, 1);
    }
    this.stats.perched = perched;
  }

  private stepGlassdarts(dt: number, px: number, py: number, pz: number): void {
    const target = Math.min(this.budget.glassdart, KIND_OF.glassdart.cap);
    while (this.glassdarts.length < target && this.waterPts.length) {
      const home = this.waterPts[Math.floor(this.rng() * this.waterPts.length)];
      this.glassdarts.push(spawnGlassdart(this.rng, home)); this.stats.glassdartsSpawned++;
    }
    for (let i = this.glassdarts.length - 1; i >= 0; i--) {
      const g = this.glassdarts[i];
      if (g.tame && g.mode === 'hover') g.timer = Math.max(g.timer, 1); // a close-up for a picture: it holds its hover
      stepGlassdart(g, dt, this.rng);
      if (g.darted) {
        this.stats.darts++;
        const d = Math.hypot(g.x - px, g.y - py, g.z - pz), t = performance.now();
        if (d < 5 && t - this.lastBuzz > SCOPE_SOUND_COOLDOWN.buzz) { this.lastBuzz = t; this.stats.buzzes++; sfxAt('sfx_critter_dragonfly', { x: g.x, y: g.y, z: g.z }, { gain: 0.35 * (1 - d / 5), rate: 0.9 + this.rng() * 0.25, cooldownMs: 300 }); }
      }
      if (i >= target || Math.hypot(g.home.x - px, g.home.z - pz) > GLASSDART.searchM * 1.6) this.glassdarts.splice(i, 1);
    }
  }

  private stepTideclacks(dt: number, px: number, pz: number, near: { x: number; z: number }): void {
    const target = Math.min(this.budget.tideclack, KIND_OF.tideclack.cap);
    while (this.tideclacks.length < target && this.shorePts.length) {
      const shore = this.shorePts[Math.floor(this.rng() * this.shorePts.length)];
      this.tideclacks.push(spawnTideclack(this.rng, shore)); this.stats.tideclacksSpawned++;
    }
    const belowSea = (x: number, z: number): boolean => { const l = this.stillAt(x, z); return l !== null && this.groundAt(x, z) < l - 0.02; };
    for (let i = this.tideclacks.length - 1; i >= 0; i--) {
      const c = this.tideclacks[i];
      if (c.tame && c.mode === 'pause') c.timer = Math.max(c.timer, 1); // a close-up for a picture: it holds its pause
      stepTideclack(c, dt, this.rng, c.tame ? null : near, belowSea);
      if (c.started) {
        const d = Math.hypot(c.x - px, c.z - pz), t = performance.now();
        if (d < 7 && t - this.lastClick > SCOPE_SOUND_COOLDOWN.click) { this.lastClick = t; this.stats.clicks++; sfxAt('sfx_critter_crab', { x: c.x, y: this.groundAt(c.x, c.z) + 0.05, z: c.z }, { gain: 0.3 * (1 - d / 7), rate: 0.85 + this.rng() * 0.3, cooldownMs: 150 }); }
      }
      if (c.gone) { this.stats.crabsFled++; this.tideclacks.splice(i, 1); continue; }
      if (i >= target || Math.hypot(c.x - px, c.z - pz) > TIDECLACK.searchM * 1.5) this.tideclacks.splice(i, 1);
    }
  }

  // ─── drawing ───────────────────────────────────────────────────────────────
  private draw(): void {
    const hidden = atlas.interiorId !== null;
    const sb = this.batch('sunwing'), gb = this.batch('glassdart'), cb = this.batch('tideclack');
    if (sb) {
      sb.begin();
      for (const s of this.sunwings) {
        const ang = sunwingWingAngle(s);
        const prev = wingAngleAt(s.perched, s.flapPrev); // never a spread per insect per frame
        sb.put(s.x - this.ax, s.y - this.ay, s.z - this.az, s.yaw, s.span, ang, prev, 0, 0, s.tint);
      }
      sb.end(hidden);
    }
    if (gb) {
      gb.begin();
      for (const g of this.glassdarts) gb.put(g.x - this.ax, g.y - this.ay, g.z - this.az, g.yaw, g.span, Math.sin(g.buzz) * GLASSDART.buzzAmp, Math.sin(g.buzzPrev) * GLASSDART.buzzAmp, 0, 0);
      gb.end(hidden);
    }
    if (cb) {
      cb.begin();
      for (const c of this.tideclacks) cb.put(c.x - this.ax, this.groundAt(c.x, c.z) - this.ay, c.z - this.az, c.yaw, c.size, c.gait, c.gaitPrev, c.mode === 'pause' ? 0 : 1, 0);
      cb.end(hidden);
    }
  }

  private ensureAnchor(px: number, py: number, pz: number): void {
    if (!this.anchor) { this.anchor = new THREE.Group(); this.anchor.name = 'ambient-critters'; this.scene!.add(this.anchor); this.ax = Number.NaN; }
    if (!(Math.hypot(px - this.ax, pz - this.az) < REANCHOR_M)) {
      this.ax = px; this.ay = py; this.az = pz;
      this.anchor.position.set(px, py, pz);
      this.anchor.updateMatrixWorld(true);
    }
  }

  private catalogId(prefabKey: string): bigint | null {
    const conn = atlas.conn;
    if (!conn) return null;
    try { for (const pm of (conn.db.prefabModel as unknown as { iter: () => Iterable<{ prefabKey: string; assetId: bigint }> }).iter()) if (pm.prefabKey === prefabKey) return pm.assetId; } catch { /* churn */ }
    return null;
  }

  private batch(kind: Kind): CreatureBatch | null {
    let slot = this.slots.get(kind);
    if (!slot) { slot = { batch: null, loading: false, failed: 0 }; this.slots.set(kind, slot); }
    if (slot.batch) return slot.batch;
    const wanted = kind === 'sunwing' ? this.sunwings.length : kind === 'glassdart' ? this.glassdarts.length : this.tideclacks.length;
    if (wanted > 0 && !slot.loading && slot.failed < 3 && this.assets && this.anchor && performance.now() >= (slot.retryAt ?? 0)) void this.load(kind, slot);
    return null;
  }

  private async load(kind: Kind, slot: Slot): Promise<void> {
    const spec = KIND_OF[kind];
    const id = this.catalogId(spec.prefab);
    // A body not in the catalogue (a shard without the prop, or the catalogue still on the wire) was looked up for every
    // animal every frame — an async call and a whole-table scan each. Ask again in a second.
    if (id === null || !this.assets || !this.anchor) { slot.retryAt = performance.now() + 1000; return; }
    slot.loading = true;
    try {
      const { object, release } = await this.assets.loadRepresentation(id, 2, { urgent: true }); // THE QUEUE LAW: a creature in view needs its body now
      if (this.disposed || !this.anchor) { release(); return; }
      const parts = creatureParts(object, spec.kind, { layFlat: spec.layFlat, tinted: spec.tinted });
      if (!parts) { release(); slot.failed++; return; }
      slot.batch = new CreatureBatch(this.anchor, parts, spec.cap, `critter:${kind}`, release);
    } catch { slot.failed++; }
    finally { slot.loading = false; }
  }

  // ─── levers ────────────────────────────────────────────────────────────────
  /** Force critters here (or `dist` m ahead). `tame` ones ignore people and hold still — close-up pictures only. */
  private force(kind: Kind, n: number, dist?: number, tame = false): Record<string, unknown> {
    // in front of the camera when asked: three's camera forward is (−sin yaw, −cos yaw)
    const yaw = atlas.localYaw ?? 0;
    const ahead = dist !== undefined && Number.isFinite(dist);
    const px = ahead ? atlas.localX - Math.sin(yaw) * dist! : atlas.localX, pz = ahead ? atlas.localZ - Math.cos(yaw) * dist! : atlas.localZ;
    const before = { s: this.sunwings.length, g: this.glassdarts.length, t: this.tideclacks.length };
    if (kind === 'sunwing') for (let i = 0; i < n; i++) this.sunwings.push(spawnSunwing(this.rng, px, pz, (x, z) => this.groundAt(x, z)));
    if (kind === 'glassdart') {
      const home = this.waterPts[0] ?? { x: px, z: pz, y: this.groundAt(px, pz) + 0.3 };
      for (let i = 0; i < n; i++) this.glassdarts.push(spawnGlassdart(this.rng, home));
    }
    if (kind === 'tideclack') {
      const shore = this.shorePts[0] ?? { x: px + 4, z: pz, y: this.groundAt(px + 4, pz), sx: 1, sz: 0 };
      for (let i = 0; i < n; i++) this.tideclacks.push(spawnTideclack(this.rng, shore));
    }
    if (ahead) {
      // gather them in a tight group at the chosen spot (a butterfly flies there, a dragonfly hovers, a crab rests)
      const spread = Math.max(0.6, dist! * 0.12);
      const gy = this.groundAt(px, pz);
      for (const s of this.sunwings.slice(before.s)) { s.x = px + (this.rng() - 0.5) * spread; s.z = pz + (this.rng() - 0.5) * spread; s.h = 1.1 + this.rng() * 0.6; s.y = gy + s.h; s.timer = 6; s.vx *= 0.3; s.vz *= 0.3; }
      for (const d of this.glassdarts.slice(before.g)) { d.home = { x: px, z: pz, y: gy + 0.4 }; d.x = px + (this.rng() - 0.5) * spread; d.z = pz + (this.rng() - 0.5) * spread; d.y = gy + 0.9 + this.rng() * 0.5; d.mode = 'hover'; d.timer = 6; }
      for (const c of this.tideclacks.slice(before.t)) { c.x = px + (this.rng() - 0.5) * spread; c.z = pz + (this.rng() - 0.5) * spread; c.mode = 'pause'; c.timer = 8; }
    }
    if (tame) {
      for (const s of this.sunwings.slice(before.s)) s.tame = true;
      for (const d of this.glassdarts.slice(before.g)) d.tame = true;
      for (const c of this.tideclacks.slice(before.t)) c.tame = true;
    }
    // a forced flight outlives the budget only for this frame: raise the budget so it is kept
    this.budget = { ...this.budget, [kind]: Math.max(this.budget[kind], ahead ? this.budget[kind] + n : n) };
    this.envIn = 30; // hold the forced budget for 30 s before the place is judged again
    return { kind, n, sunwings: this.sunwings.length, glassdarts: this.glassdarts.length, tideclacks: this.tideclacks.length };
  }

  private diag(): Record<string, unknown> {
    const nearest = (xs: Array<{ x: number; z: number }>): number => xs.reduce((m, e) => Math.min(m, Math.hypot(e.x - atlas.localX, e.z - atlas.localZ)), Infinity);
    return {
      gate: this.gate, budget: this.budget, env: this.lastEnv,
      sunwings: this.sunwings.length, glassdarts: this.glassdarts.length, tideclacks: this.tideclacks.length,
      waterPoints: this.waterPts.length, shorePoints: this.shorePts.length,
      nearestM: { sunwing: Math.round(nearest(this.sunwings) * 10) / 10, glassdart: Math.round(nearest(this.glassdarts) * 10) / 10, tideclack: Math.round(nearest(this.tideclacks) * 10) / 10 },
      models: Object.fromEntries([...this.slots].map(([k, s]) => [k, s.batch ? `ready (${s.batch.count} drawn)` : s.loading ? 'loading' : s.failed ? `failed ×${s.failed}` : 'waiting'])),
      stats: { ...this.stats },
    };
  }

  private clearAll(): void {
    this.sunwings = []; this.glassdarts = []; this.tideclacks = [];
    for (const s of this.slots.values()) { s.batch?.begin(); s.batch?.end(true); }
  }

  dispose(): void {
    this.disposed = true;
    this.clearAll();
    for (const s of this.slots.values()) s.batch?.dispose();
    this.slots.clear();
    this.anchor?.removeFromParent();
    this.anchor = null;
  }
}
